import { useState, useEffect } from 'react';
import { X, Plus, Minus } from 'lucide-react';

interface Modifier {
  id: string;
  name: string;
  price: number;
  count?: number;
}

interface DishModalProps {
  isOpen: boolean;
  onClose: () => void;
  dish: any;
  modifiers: Modifier[];
  onAddToCart: (id: string, quantity: number, modifiers: Modifier[]) => void;
}

const DishModal: React.FC<DishModalProps> = ({ isOpen, onClose, dish, modifiers, onAddToCart }) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedModifiers, setSelectedModifiers] = useState<Record<string, number>>({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Пересчёт цены
  useEffect(() => {
    if (!dish) return;
    let price = dish.price;
    Object.entries(selectedModifiers).forEach(([modifierId, count]) => {
      const modifier = modifiers.find(m => m.id === modifierId);
      if (modifier && count > 0) {
        price += modifier.price * count;
      }
    });
    setTotalPrice(price * quantity);
  }, [dish, selectedModifiers, quantity, modifiers]);

  // Сброс при открытии
  useEffect(() => {
    if (isOpen) {
      setQuantity(1);
      setSelectedModifiers({});
    }
  }, [isOpen, dish]);

  const handleModifierChange = (modifierId: string, delta: number) => {
    setSelectedModifiers(prev => {
      const currentCount = prev[modifierId] || 0;
      const newCount = Math.max(0, currentCount + delta);
      if (newCount === 0) {
        const { [modifierId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [modifierId]: newCount };
    });
  };

  const handleAddToCart = () => {
    if (!dish) return;
    const selected = Object.entries(selectedModifiers)
      .map(([id, count]) => {
        const modifier = modifiers.find(m => m.id === id);
        return modifier ? { ...modifier, count } : null;
      })
      .filter(Boolean) as Modifier[];

    onAddToCart(dish.id, quantity, selected);
    onClose();
  };

  if (!isOpen || !dish) return null;

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: isMobile ? 'stretch' : 'center',
        zIndex: 9999,
        padding: isMobile ? '0' : '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: isMobile ? '24px 24px 0 0' : '24px',
          width: isMobile ? '100%' : '50%',
          maxWidth: isMobile ? '100%' : '600px',
          maxHeight: isMobile ? '90vh' : '85vh',
          overflowY: 'auto',
          padding: '24px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: '700', margin: 0, marginBottom: '8px', color: 'var(--text-primary)' }}>
              {dish.name}
            </h2>
            <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>
              {dish.price} ₽
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', color: 'var(--text-secondary)' }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Description */}
        {dish.description && (
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
            {dish.description}
          </p>
        )}

        {/* Image */}
        {dish.imageUrls && dish.imageUrls[0] && (
          <div style={{ borderRadius: '16px', overflow: 'hidden', marginBottom: '20px', height: '200px' }}>
            <img src={dish.imageUrls[0]} alt={dish.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        )}

        {/* Modifiers */}
        {modifiers.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>
              Добавить к блюду
            </h3>

            {modifiers.map((modifier) => (
              <div
                key={modifier.id}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 0', borderBottom: '1px solid var(--border-color)',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                    {modifier.name}
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    +{modifier.price} ₽
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    onClick={() => handleModifierChange(modifier.id, -1)}
                    disabled={!selectedModifiers[modifier.id]}
                    style={{
                      width: '32px', height: '32px', borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      background: selectedModifiers[modifier.id] ? 'var(--bg-secondary)' : 'transparent',
                      color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: selectedModifiers[modifier.id] ? 'pointer' : 'not-allowed',
                      opacity: selectedModifiers[modifier.id] ? 1 : 0.3,
                    }}
                  >
                    <Minus size={16} />
                  </button>
                  <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                    {selectedModifiers[modifier.id] || 0}
                  </span>
                  <button
                    onClick={() => handleModifierChange(modifier.id, 1)}
                    style={{
                      width: '32px', height: '32px', borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quantity and Add to Cart */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '12px' }}>
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', padding: '4px' }}
            >
              <Minus size={20} />
            </button>
            <span style={{ fontSize: '18px', fontWeight: '600', minWidth: '30px', textAlign: 'center', color: 'var(--text-primary)' }}>
              {quantity}
            </span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', padding: '4px' }}
            >
              <Plus size={20} />
            </button>
          </div>

          <button
            onClick={handleAddToCart}
            style={{
              flex: 1, backgroundColor: '#10b981', color: '#fff', border: 'none',
              borderRadius: '12px', padding: '16px', fontSize: '16px', fontWeight: '600',
              cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px',
            }}
          >
            В корзину &bull; {totalPrice} ₽
          </button>
        </div>
      </div>
    </div>
  );
};

export default DishModal;
